<template>
  <div class="bg-settings-component">
    <!-- 背景设置按钮 -->
    <el-tooltip content="背景设置" placement="top" :enterable="false">
      <el-button 
        class="icon-btn bg-settings-btn" 
        type="primary" 
        size="small" 
        circle
        @click="showBgSettings = true"
      >
        <i class="el-icon-picture-outline"></i>
      </el-button>
    </el-tooltip>
    
    <!-- 背景设置对话框 -->
    <el-dialog
      title="背景设置"
      :visible.sync="showBgSettings"
      width="400px"
      :close-on-click-modal="false"
      :modal="false"
      :before-close="handleDialogClose"
      custom-class="bg-settings-dialog"
      :append-to-body="true"
    >
      <div class="bg-settings-content">
        <div class="bg-select-section">
          <div class="section-title">选择背景图片</div>
          <div class="bg-images-grid">
            <div 
              v-for="(bg, index) in backgroundImages" 
              :key="index"
              class="bg-image-item"
              :class="{ 'active': selectedBgIndex === index }"
              @click="selectBackground(index)"
            >
              <img :src="bg.src" :alt="bg.name" />
            </div>
            <div 
              class="bg-image-item no-bg"
              :class="{ 'active': selectedBgIndex === -1 }"
              @click="selectBackground(-1)"
            >
              <i class="el-icon-close"></i>
              <span>无背景</span>
            </div>
          </div>
        </div>
        
        <div class="opacity-section">
          <div class="section-title">透明度设置</div>
          <!-- 修改内容区域透明度滑块，添加@input事件 -->
          <div class="opacity-item">
            <span>内容区域透明度（卡片背景）：{{ contentTransparency }}</span>
            <el-slider 
              v-model="contentTransparency" 
              :min="0" 
              :max="0.5" 
              :step="0.05"
              :show-tooltip="false"
              show-input
              @input="previewChanges"
            ></el-slider>
          </div>
          <div class="opacity-item">
            <span>背景图片透明度（整页背景）：{{ bgTransparency }}</span>
            <el-slider 
              v-model="bgTransparency" 
              :min="0" 
              :max="1" 
              :step="0.05"
              :show-tooltip="false"
              show-input
              @input="previewChanges"
            ></el-slider>
          </div>
        </div>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="cancelBgSettings">取消</el-button>
        <el-button type="primary" @click="applyBgSettings" :style="{ backgroundColor: '#409eff', color: '#ffffff', borderColor: '#409eff' }">应用</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
// 导入背景图片
const importAll = (r) => r.keys().map(key => ({
  src: r(key),
  name: key.replace(/^\.\//, '').replace(/\.\w+$/, '')
}))

// 预加载背景图片
const backgroundImageContext = require.context('@/assets/images/article-bg', false, /\.(png|jpe?g|svg)$/)
const preloadedBackgroundImages = importAll(backgroundImageContext)

export default {
  name: 'BackgroundSettings',
  props: {
    // 是否只显示按钮，不显示背景设置面板
    buttonOnly: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      // 背景相关设置
      backgroundImages: preloadedBackgroundImages,
      selectedBgIndex: -1, // -1表示无背景
      contentTransparency: 0.2, // 内容区域透明度
      bgTransparency: 1,        // 背景图透明度
      showBgSettings: false,
      // 用于存储实际应用的设置
      appliedBgIndex: -1,
      appliedContentTransparency: 0.2,
      appliedBgTransparency: 1
    }
  },
  computed: {
    // 内容卡片样式
    contentCardStyle() {
      return {
        backgroundColor: `rgba(255, 255, 255, ${1 - this.contentTransparency})`
      }
    },
    // 背景层样式
    backgroundLayerStyle() {
      if (this.selectedBgIndex === -1) return {}
      
      return {
        backgroundImage: `url(${this.backgroundImages[this.selectedBgIndex]?.src})`,
        opacity: this.bgTransparency
      }
    },
    // 页面主背景样式
    backgroundStyle() {
      return {
        backgroundColor: '#f0f2f5' // 保持原有的背景色
      }
    }
  },
  created() {
    // 加载背景设置
    this.loadBgSettings()
  },
  mounted() {
    // 监听storage变化，以便在其他页面修改设置时同步更新
    window.addEventListener('storage', this.handleStorageChange)
  },
  beforeDestroy() {
    // 移除storage事件监听
    window.removeEventListener('storage', this.handleStorageChange)
  },
  methods: {
    // 从本地存储加载背景设置
    loadBgSettings() {
      try {
        // 尝试获取全局设置
        const settings = JSON.parse(localStorage.getItem('globalBgSettings'))
        if (settings) {
          // 确保背景图索引正确加载
          this.selectedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1
          
          // 透明度设置，确保正确处理0值
          this.contentTransparency = settings.contentTransparency !== undefined ? 
                                     settings.contentTransparency : 0.2
          this.bgTransparency = settings.bgTransparency !== undefined ? 
                               settings.bgTransparency : 1
          
          // 应用加载的设置
          this.applyBgSettings(false)
        }
      } catch (e) {
        console.error('加载背景设置失败:', e)
      }
    },
    
    // 处理storage事件，当localStorage变化时更新设置
    handleStorageChange(event) {
      if (event.key === 'globalBgSettings') {
        try {
          const settings = JSON.parse(event.newValue);
          if (settings) {
            // 使用相同的逻辑处理值
            this.appliedBgIndex = settings.bgIndex !== undefined ? settings.bgIndex : -1;
            this.appliedContentTransparency = settings.contentTransparency !== undefined ? 
                                             settings.contentTransparency : 0.2;
            this.appliedBgTransparency = settings.bgTransparency !== undefined ? 
                                        settings.bgTransparency : 1;
             
            // 同步更新当前编辑中的值
            this.selectedBgIndex = this.appliedBgIndex;
            this.contentTransparency = this.appliedContentTransparency;
            this.bgTransparency = this.appliedBgTransparency;
            
            // 发送事件通知父组件背景设置已更新
            this.$emit('background-updated', {
              contentCardStyle: this.contentCardStyle,
              backgroundLayerStyle: this.backgroundLayerStyle,
              backgroundStyle: this.backgroundStyle
            });
          }
        } catch (e) {
          console.error('同步背景设置失败:', e);
        }
      }
    },
    
    // 保存背景设置到本地存储
    saveBgSettings() {
      try {
        const settings = {
          bgIndex: this.appliedBgIndex,
          contentTransparency: this.appliedContentTransparency,
          bgTransparency: this.appliedBgTransparency
        }
        // 保存为全局设置
        localStorage.setItem('globalBgSettings', JSON.stringify(settings))
      } catch (e) {
        console.error('保存背景设置失败:', e)
      }
    },
    
    // 选择背景图片
    selectBackground(index) {
      this.selectedBgIndex = index
      // 实时预览背景图变化
      this.previewChanges()
    },
    
    // 添加新方法：预览变更
    previewChanges() {
      // 发送事件通知父组件背景设置已更新（仅预览）
      this.$emit('background-updated', {
        contentCardStyle: this.contentCardStyle,
        backgroundLayerStyle: this.backgroundLayerStyle,
        backgroundStyle: this.backgroundStyle
      })
    },
    
    // 应用背景设置
    applyBgSettings(closeDialog = true) {
      // 先清理可能存在的tooltip
      const tooltips = document.querySelectorAll('.el-tooltip__popper');
      tooltips.forEach(tooltip => {
        if (tooltip && tooltip.parentNode) {
          tooltip.parentNode.removeChild(tooltip);
        }
      });
      
      this.appliedBgIndex = this.selectedBgIndex
      this.appliedContentTransparency = this.contentTransparency
      this.appliedBgTransparency = this.bgTransparency
      
      // 保存设置
      this.saveBgSettings()
      
      // 关闭对话框
      if (closeDialog) {
        this.showBgSettings = false
      }
      
      // 发送事件通知父组件背景设置已更新
      this.$emit('background-updated', {
        contentCardStyle: this.contentCardStyle,
        backgroundLayerStyle: this.backgroundLayerStyle,
        backgroundStyle: this.backgroundStyle
      });
    },
    
    // 取消背景设置
    cancelBgSettings() {
      this.showBgSettings = false;
      // 恢复应用的设置为加载的设置
      this.selectedBgIndex = this.appliedBgIndex;
      this.contentTransparency = this.appliedContentTransparency;
      this.bgTransparency = this.appliedBgTransparency;
      
      // 恢复原始背景设置
      this.$emit('background-updated', {
        contentCardStyle: {
          backgroundColor: `rgba(255, 255, 255, ${1 - this.appliedContentTransparency})`
        },
        backgroundLayerStyle: this.appliedBgIndex === -1 ? {} : {
          backgroundImage: `url(${this.backgroundImages[this.appliedBgIndex]?.src})`,
          opacity: this.appliedBgTransparency
        },
        backgroundStyle: {
          backgroundColor: '#f0f2f5'
        }
      });
    },
    
    // 关闭背景设置对话框时的回调
    handleDialogClose(done) {
      this.cancelBgSettings();
      done();
    }
  }
}
</script>

<style lang="scss" scoped>
/* 图标按钮通用样式 - PC端保持原来的蓝底+白图标 */
.icon-btn {
  background-color: #409eff;
  color: #ffffff;
  border-color: #409eff;
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.2);
  transform: translateY(0);
  transition: all 0.3s ease;
  
  i {
    font-size: 20px;
    margin: 0;
    line-height: 1;
  }
  
  &:hover, &:focus {
    background-color: #66b1ff;
    color: #ffffff;
    border-color: #66b1ff;
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.25);
    transform: translateY(-2px);
  }
  
  &:active {
    background-color: #3a8ee6;
    color: #ffffff;
    border-color: #3a8ee6;
    box-shadow: 0 2px 5px rgba(0, 0, 0, 0.15);
    transform: translateY(1px);
  }
}

/* 移动端背景设置按钮样式 - 白底+蓝图标 */
@media screen and (max-width: 768px) {
  .icon-btn {
    background-color: rgba(255, 255, 255, 0.7) !important;
    color: #409eff !important;
    border-color: rgba(255, 255, 255, 0.7) !important;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
    
    i {
      font-size: 20px !important;
    }
    
    &:hover, &:focus {
      background-color: rgba(255, 255, 255, 0.9) !important;
      color: #409eff !important;
      border-color: rgba(255, 255, 255, 0.9) !important;
      transform: scale(1.05) !important;
    }
    
    &:active {
      background-color: rgba(255, 255, 255, 1) !important;
      color: #409eff !important;
      border-color: rgba(255, 255, 255, 1) !important;
      transform: scale(0.95) !important;
    }
  }
}

/* 背景设置对话框样式 */
:deep(.bg-settings-dialog) {
  /* 设置对话框背景 */
  .el-dialog {
    background-color: rgba(255, 255, 255, 0.95);
    z-index: 9999 !important; /* 增加z-index确保不被遮挡 */
  }
  
  .el-dialog__body {
    padding: 20px;
  }
  
  .bg-settings-content {
    .section-title {
      font-size: 16px;
      font-weight: 500;
      margin-bottom: 15px;
      color: #303133;
    }
    
    .bg-select-section {
      margin-bottom: 20px;
      
      .bg-images-grid {
        display: grid;
        grid-template-columns: repeat(3, 1fr);
        gap: 10px;
        
        .bg-image-item {
          height: 80px;
          border: 2px solid transparent;
          border-radius: 4px;
          overflow: hidden;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          
          img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          
          &.active {
            border-color: #409eff;
            box-shadow: 0 0 8px rgba(64, 158, 255, 0.6);
          }
          
          &.no-bg {
            background-color: #f5f7fa;
            color: #909399;
            flex-direction: column;
            
            i {
              font-size: 24px;
              margin-bottom: 5px;
            }
            
            span {
              font-size: 12px;
            }
          }
          
          &:hover {
            transform: scale(1.05);
          }
        }
      }
    }
    
    .opacity-section {
      .opacity-item {
        margin-bottom: 15px;
        
        span {
          display: block;
          margin-bottom: 8px;
          font-size: 14px;
          color: #606266;
        }
      }
    }
  }
}
</style>

<style>
/* 全局样式，修复对话框遮罩层问题 */
.v-modal {
  z-index: 9998 !important; /* 确保遮罩层z-index比普通内容高 */
}

.el-dialog__wrapper.bg-settings-dialog {
  z-index: 9999 !important; /* 确保对话框容器z-index足够高 */
}
</style> 